import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  History, RefreshCw, Loader2, Filter, X, ChevronDown, ChevronUp,
  LogIn, LogOut, Plus, Edit3, Trash, Eye
} from 'lucide-react';

const ACTION_ICONS = {
  login: { Icon: LogIn, color: 'bg-blue-100 text-blue-700' },
  logout: { Icon: LogOut, color: 'bg-slate-100 text-slate-600' },
  create: { Icon: Plus, color: 'bg-green-100 text-green-700' },
  update: { Icon: Edit3, color: 'bg-amber-100 text-amber-700' },
  delete: { Icon: Trash, color: 'bg-red-100 text-red-700' },
  view: { Icon: Eye, color: 'bg-purple-100 text-purple-700' },
};

const ENTITY_TYPES = [
  'devis', 'gestion-clients', 'sites', 'fiches-visite', 'chantiers',
  'sous-traitants', 'administratif', 'contacts', 'user', 'auth', 'admin',
];

const ACTIONS = ['login', 'logout', 'create', 'update', 'delete'];

const renderValue = (v) => {
  if (v == null) return <span className="text-slate-400 italic">vide</span>;
  if (typeof v === 'object') return <code className="text-[10px]">{JSON.stringify(v).slice(0, 80)}</code>;
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + '…' : s;
};

export default function ActivityLog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterUserId, setFilterUserId] = useState(searchParams.get('user_id') || '');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterUserId) params.user_id = filterUserId;
      if (filterEntity) params.entity_type = filterEntity;
      if (filterAction) params.action = filterAction;
      const r = await api.get('/admin/activity-log', { params });
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterUserId, filterEntity, filterAction]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setFilterUserId('');
    setFilterEntity('');
    setFilterAction('');
    setSearchParams({});
  };

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch (_) { return iso; }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="activity-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <History className="w-6 h-6 text-blue-600" />
            Journal d'activité
          </h1>
          <p className="text-slate-500 text-sm mt-1">Toutes les actions des utilisateurs : connexions, création, modification, suppression — avec diff des champs.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualiser
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-slate-400" />
          <div className="flex-1 min-w-[180px]">
            <Input value={filterUserId} onChange={e => setFilterUserId(e.target.value)}
              placeholder="Filtrer par user_id (ex: USR-...)" className="h-9 text-sm" />
          </div>
          <Select value={filterEntity || 'all'} onValueChange={v => setFilterEntity(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-9 w-48 text-sm" data-testid="filter-entity">
              <SelectValue placeholder="Tous les modules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les modules</SelectItem>
              {ENTITY_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterAction || 'all'} onValueChange={v => setFilterAction(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-9 w-40 text-sm" data-testid="filter-action">
              <SelectValue placeholder="Toutes actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes actions</SelectItem>
              {ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filterUserId || filterEntity || filterAction) && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="w-3 h-3 mr-1" /> Réinitialiser
            </Button>
          )}
          <span className="text-xs text-slate-400 ml-auto">{total} entrées au total · {logs.length} affichées</span>
        </div>
      </div>

      {/* Logs list */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 text-slate-400 text-sm">Aucune entrée correspondante.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-50">
          {logs.map(lg => {
            const info = ACTION_ICONS[lg.action] || ACTION_ICONS.view;
            const Icon = info.Icon;
            const hasDiff = lg.diff && Object.keys(lg.diff).length > 0;
            const expanded = expandedLog === lg.log_id;
            return (
              <div key={lg.log_id} className="p-3 hover:bg-slate-50/50" data-testid={`log-${lg.log_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${info.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-semibold text-slate-900">{lg.user?.name || lg.user?.email || 'Anonyme'}</span>
                      <span className="text-slate-500"> · {lg.action} </span>
                      <span className="text-slate-700 font-medium">{lg.entity_type}</span>
                      {lg.entity_label && <span className="text-slate-500"> → {lg.entity_label}</span>}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {formatTime(lg.timestamp)} · IP {lg.ip || 'n/a'} · {lg.user?.role || 'user'}
                    </p>
                  </div>
                  {hasDiff && (
                    <button onClick={() => setExpandedLog(expanded ? null : lg.log_id)}
                      className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                      data-testid={`log-${lg.log_id}-toggle`}>
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                {expanded && hasDiff && (
                  <div className="mt-3 ml-11 bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Changements ({Object.keys(lg.diff).length} champ{Object.keys(lg.diff).length > 1 ? 's' : ''}) :</p>
                    <div className="space-y-2">
                      {Object.entries(lg.diff).map(([field, change]) => (
                        <div key={field} className="text-xs">
                          <span className="font-mono font-semibold text-slate-700">{field}</span>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <div className="bg-red-50 border border-red-100 rounded p-2">
                              <p className="text-[10px] text-red-600 uppercase font-semibold mb-0.5">Avant</p>
                              <p className="text-slate-700 break-words">{renderValue(change.before)}</p>
                            </div>
                            <div className="bg-green-50 border border-green-100 rounded p-2">
                              <p className="text-[10px] text-green-600 uppercase font-semibold mb-0.5">Après</p>
                              <p className="text-slate-700 break-words">{renderValue(change.after)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
